import type { WorkbookTabs } from './workbookSession';

/**
 * The tabs with `id` open and shown. What an add tab (`from`) just created
 * takes that tab's place when it is the one shown, as a spreadsheet's Insert
 * dialog closes once the thing is made; otherwise `id` opens after the rest.
 *
 * With `preview`, `id` opens as the temporary tab, as an editor's single
 * click does: it takes the place of the tab that was temporary, so browsing
 * leaves one tab behind rather than one per click. A tab already open is
 * only shown, and opening without `preview` keeps a temporary tab for good.
 */
export function openTab(
  tabs: WorkbookTabs,
  id: string,
  from?: string,
  preview = false,
): WorkbookTabs {
  const { opened, selected } = tabs;
  const current = tabs.preview !== undefined && opened.includes(tabs.preview);
  const temporary = current ? tabs.preview : undefined;
  if (from !== undefined && selected === from && opened.includes(from))
    return {
      opened: opened.flatMap((item) =>
        item === from ? (opened.includes(id) ? [] : [id]) : [item],
      ),
      selected: id,
      preview: temporary === id ? undefined : temporary,
    };
  if (opened.includes(id))
    return {
      opened,
      selected: id,
      preview: preview || temporary !== id ? temporary : undefined,
    };
  if (preview && temporary !== undefined)
    return {
      opened: opened.map((item) => (item === temporary ? id : item)),
      selected: id,
      preview: id,
    };
  return {
    opened: [...opened, id],
    selected: id,
    preview: preview ? id : temporary,
  };
}
